// The curated lists of miami-todo.com: the categories of the strip, the moods
// of "Curated for Your Mood" and the neighbourhoods. Each is a page at
// /[lang]/<slug>, meant to list the tours whose `tours.themes` contain its
// `theme` (the tours_by_theme RPC), as on dubai-todo.com.
//
// No data yet: the `theme` values are the rule names that supabase/sites/miami.sql
// is to give tour_theme_rules (stage 4 of docs/miami-plan.md, checked against
// the live Viator Miami products then — the categories may still change). Until
// then every page shows its empty state.
//
// Only categories with an affiliate catalog behind them — the mockup's
// Restaurants, Nightlife, Beach Clubs and Wellness are left out (the owner's
// decision, 2026-10-31). Data only — the shared components read it through
// SITE_UI (src/site-ui.ts); the helpers over it are @todo/web-ui/lib/collections.
// Copy (titles, intros) lives in the i18n resources under `collections.<slug>`.

import { awinDeepLink } from '@todo/core/awin';
import type { Collection, PartnerLink, StripItem } from '@todo/web-ui/lib/collections';
import type { GuideKey } from '@todo/web-ui/lib/guides';

/**
 * Sailo (boat rentals and yacht charters, ~1,000 boats in Miami; no feed): an
 * AWIN deep link to a search on sailo.com, under the boat pages' tours
 * (PartnerLinks). Programme 92667 «SAILO, INC», publisher miami-todo 3142764;
 * the page's slug as `clickref`. docs/sailo-integration.md.
 */
function sailo(slug: string, path: string): PartnerLink {
  return {
    source: 'sailo',
    kind: 'boat',
    name: 'Sailo',
    url: awinDeepLink({ advertiserId: 92667, publisherId: 3142764 }, `https://www.sailo.com/boat-rentals/${path}`, slug),
  };
}

export const COLLECTIONS: readonly Collection[] = [
  // Category strip, in its order — see CATEGORY_STRIP. /boats ("Yachts &
  // Boats") is a collection too: Viator's boat tours (the yacht_charter rule),
  // as Miami has no Click&Boat catalog to fill dubai's boats table.
  { slug: 'boats', theme: 'yacht_charter', kind: 'category', icon: 'M12 3v13M12 4l6 11h-6M12 6l-5 9h5M3 18h18l-2 3H5z', partnerLinks: [sailo('boats', 'FL/Miami')] },
  { slug: 'everglades', theme: 'everglades', kind: 'category', icon: 'M3 15h12l3-3H6zM16 12V9M18 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 1 0 0-5M2 19c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M4 12V8M7 12V6' },
  { slug: 'city-tours', theme: 'city_tour', kind: 'category', icon: 'M9 21V9l3-5 3 5v12M6 21v-8h3M15 13h3v8M4 21h16M12 4V2M11 12h2M11 16h2' },
  { slug: 'day-trips', theme: 'day_trip', kind: 'category', icon: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14' },
  { slug: 'water-sports', theme: 'water_sport', kind: 'category', icon: 'M2 16c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M2 20c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M8 11l4-7 5 4' },
  { slug: 'attractions', theme: 'attraction', kind: 'category', icon: 'M4 7h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4zM10 7v10' },
  { slug: 'family', theme: 'family', kind: 'category', icon: 'M8 8a2.5 2.5 0 1 0 0-.01M16 9.5a2 2 0 1 0 0-.01M3.5 20c.5-4 2.3-6.5 4.5-6.5s4 2.5 4.5 6.5M12.5 20c.4-3 1.8-5 3.5-5s3.1 2 3.5 5' },

  // Topics of /boats (#126): Viator's yachts and sandbar trips, each with
  // Sailo's search for the rest — its motor yachts in Miami; the boats around
  // Haulover (Sunny Isles Beach, the closest of Sailo's areas). Before the
  // moods: were beach-days (whose rule takes the sandbars too) ever to copy
  // sandbar-boat-tours, beach-days is the page that drops out — 8 of its 12
  // tours shared on 2026-11-03, 67 % (the build logs it from 50 %). All 8
  // sandbar tours are in beach-days' list, so the two pages answer different
  // searches only by their copy: beach-days is the beaches on land and the
  // day out, sandbar-boat-tours the sandbars — each links the other (seeAlso).
  { slug: 'yacht-rental', theme: 'yacht_rental', kind: 'topic', parent: 'boats', icon: 'M3 15h18l-3 5H6zM6 15l2-5h8l3 5M9 10l1-3h4l1 3M12 7V4', partnerLinks: [sailo('yacht-rental', 'FL/Miami?length_min=40&boat_type%5B%5D=motor')] },
  { slug: 'sandbar-boat-tours', theme: 'sandbar', kind: 'topic', parent: 'boats', seeAlso: ['beach-days'], icon: 'M2 18c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M4 14c3-3 13-3 16 0M12 4v8M12 5l5 4h-5', partnerLinks: [sailo('sandbar-boat-tours', 'FL/Sunny_Isles_Beach')] },

  // Topic of the luxury mood: helicopter and small-plane flights ("miami
  // helicopter tour" — thousands a month, no Viator / GetYourGuide in the top
  // 10 on 2026-11-03). 8 of its 10 flights are in date-night and in luxury
  // too, so those two keep the romantic / indulgent searches and link here.
  { slug: 'helicopter-tours', theme: 'scenic_flight', kind: 'topic', parent: 'luxury', seeAlso: ['date-night'], icon: 'M3 6h18M12 6v3M6 9h10a3 3 0 0 1 3 3v1a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3zM19 12h3M9 16l-1 3M15 16l1 3M6 19h12' },

  // "Curated for Your Mood" (with the family category — MOOD_TILES).
  { slug: 'date-night', theme: 'date_night', kind: 'mood', seeAlso: ['helicopter-tours'], icon: 'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.3a4.3 4.3 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z' },
  { slug: 'beach-days', theme: 'beach', kind: 'mood', seeAlso: ['sandbar-boat-tours'], icon: 'M12 4a7 7 0 0 1 7 7H5a7 7 0 0 1 7-7zM12 11v9M3 20h18M12 4V3' },
  { slug: 'luxury', theme: 'luxury', kind: 'mood', icon: 'M6 4h12l3 5-9 11L3 9zM3 9h18M9 4l3 16M15 4l-3 16' },
  { slug: 'art-and-culture', theme: 'culture', kind: 'mood', icon: 'M4 21h16M5 21V10M19 21V10M9 21V10M15 21V10M3 10l9-6 9 6z' },

  // Neighbourhoods (footer, "Areas"). Miami is one `cities` row; an area is a
  // theme too, e.g. keywords "south beach|ocean drive" for south-beach.
  { slug: 'south-beach', theme: 'area_south_beach', kind: 'area', icon: 'M12 4a7 7 0 0 1 7 7H5a7 7 0 0 1 7-7zM12 11v9M3 20h18' },
  { slug: 'wynwood', theme: 'area_wynwood', kind: 'area', icon: 'M4 21V8h16v13M4 12h16M8 16h2M14 16h2M2 21h20' },
  { slug: 'little-havana', theme: 'area_little_havana', kind: 'area', icon: 'M4 21V11l8-6 8 6v10M9 21v-6h6v6M2 21h20' },
  { slug: 'brickell', theme: 'area_brickell', kind: 'area', icon: 'M5 22V8h4v14M15 22V4h4v18M2 22h20' },
];

/** The "Read also" guides of the hubs that aren't collections — none until the site has articles. */
export const HUB_GUIDES: Readonly<Record<string, readonly GuideKey[]>> = {};

/**
 * The category strip: the seven categories, Boat Tours & Cruises first (a 4 + 3
 * grid on phones — `--strip-columns` in src/global.css). The mockup's Events &
 * Festivals tile is left out until the site has an events source.
 */
export const CATEGORY_STRIP: StripItem[] = COLLECTIONS.filter((c) => c.kind === 'category').map((collection) => ({
  kind: 'collection' as const,
  collection,
}));
